/** Heuristic recommendations for action-first dashboard. */

/**
 * @typedef {Object} DashboardStats
 * @property {number} totalErrors
 * @property {number} metaErrors
 * @property {number} contentErrors
 * @property {number} imageErrors
 * @property {number} brokenErrors
 * @property {number} overallScore
 * @property {{ current: number, total: number, trialLimit?: number }} pagesAudited
 * @property {{ isCached?: boolean, outdatedCount?: number, unvalidatedCount?: number }} [cacheMetadata]
 * @property {{ optimized: number, total: number }} [imageOptimization]
 * @property {{ generated: number, total: number }} [altTextGeneration]
 */

export function altTextOpportunity(stats) {
  const fromErrors = stats.imageErrors ?? 0;
  if (fromErrors > 0) return fromErrors;
  const total = stats.imageOptimization?.total ?? stats.altTextGeneration?.total ?? 0;
  const generated = stats.altTextGeneration?.generated ?? 0;
  return Math.max(0, total - generated);
}

/**
 * Returns i18n keys (+ params) for the recommended action.
 * UI must call t(titleKey, titleParams) / t(ctaKey) / t(timeKey).
 */
export function computeRecommendedAction(stats) {
  const altOpp = altTextOpportunity(stats);

  if (!stats.cacheMetadata?.isCached) {
    return {
      id: 'first-audit',
      titleKey: 'dashboardActionFirst.recFirstAuditTitle',
      titleParams: {},
      impact: 'High',
      scoreGain: 0,
      timeKey: 'dashboardActionFirst.recTimeAbout2Min',
      ctaKey: 'dashboardActionFirst.recFirstAuditCta',
      navigate: { tab: 'dashboard', quickAction: 'validate-all' },
    };
  }

  if (altOpp >= 5) {
    return {
      id: 'alt-text',
      titleKey: 'dashboardActionFirst.recAltTextTitle',
      titleParams: { count: altOpp },
      impact: 'High',
      scoreGain: Math.min(15, Math.max(3, Math.ceil(altOpp * 0.12))),
      timeKey: altOpp > 50 ? 'dashboardActionFirst.recTimeAbout5Min' : 'dashboardActionFirst.recTimeAbout1Min',
      ctaKey: 'dashboardActionFirst.recAltTextCta',
      navigate: { tab: 'alt-text-generator' },
    };
  }

  if ((stats.metaErrors ?? 0) >= (stats.contentErrors ?? 0) && stats.metaErrors > 0) {
    return {
      id: 'meta-seo',
      titleKey: 'dashboardActionFirst.recMetaTitle',
      titleParams: { count: stats.metaErrors },
      impact: 'High',
      scoreGain: Math.min(12, Math.max(2, Math.ceil(stats.metaErrors * 0.04))),
      timeKey: 'dashboardActionFirst.recTimeAbout3Min',
      ctaKey: 'dashboardActionFirst.recTryAiSeoCta',
      navigate: { tab: 'seo-optimizer', subTab: 'overview' },
    };
  }

  if (stats.brokenErrors > 0) {
    return {
      id: 'broken',
      titleKey: 'dashboardActionFirst.recBrokenTitle',
      titleParams: { count: stats.brokenErrors },
      impact: 'Medium',
      scoreGain: Math.min(10, Math.max(2, Math.ceil(stats.brokenErrors * 0.03))),
      timeKey: 'dashboardActionFirst.recTimeAbout5Min',
      ctaKey: 'dashboardActionFirst.recViewSeoCta',
      navigate: { tab: 'seo-optimizer', subTab: 'overview' },
    };
  }

  if (stats.contentErrors > 0) {
    return {
      id: 'content',
      titleKey: 'dashboardActionFirst.recContentTitle',
      titleParams: { count: stats.contentErrors },
      impact: 'Medium',
      scoreGain: Math.min(8, Math.max(2, Math.ceil(stats.contentErrors * 0.03))),
      timeKey: 'dashboardActionFirst.recTimeAbout5Min',
      ctaKey: 'dashboardActionFirst.recTryAiSeoCta',
      navigate: { tab: 'seo-optimizer', subTab: 'overview' },
    };
  }

  const unoptimized = Math.max(
    0,
    (stats.imageOptimization?.total ?? 0) - (stats.imageOptimization?.optimized ?? 0)
  );
  if (unoptimized >= 10) {
    return {
      id: 'compress',
      titleKey: 'dashboardActionFirst.recCompressTitle',
      titleParams: { count: unoptimized },
      impact: 'Medium',
      scoreGain: 5,
      timeKey: 'dashboardActionFirst.recTimeAbout2Min',
      ctaKey: 'dashboardActionFirst.recOptimizeImagesCta',
      navigate: { tab: 'image-optimizer' },
    };
  }

  if ((stats.cacheMetadata?.outdatedCount ?? 0) > 0) {
    const n = stats.cacheMetadata.outdatedCount;
    return {
      id: 'refresh',
      titleKey: 'dashboardActionFirst.recRefreshTitle',
      titleParams: { count: n },
      impact: 'Low',
      scoreGain: 2,
      timeKey: 'dashboardActionFirst.recTimeAbout2Min',
      ctaKey: 'dashboardActionFirst.recRefreshCta',
      navigate: { tab: 'dashboard', quickAction: 'refresh-outdated' },
    };
  }

  return {
    id: 'explore',
    titleKey: 'dashboardActionFirst.recExploreTitle',
    titleParams: {},
    impact: 'Low',
    scoreGain: 0,
    timeKey: 'dashboardActionFirst.recTimeAbout1Min',
    ctaKey: 'dashboardActionFirst.recExploreCta',
    navigate: { tab: 'seo-optimizer', subTab: 'overview' },
  };
}

export function computeTopOpportunities(stats) {
  const altOpp = altTextOpportunity(stats);
  const items = [
    {
      id: 'alt',
      labelKey: 'dashboardActionFirst.oppMissingAltText',
      count: altOpp,
      impact: 'High',
      navigate: { tab: 'alt-text-generator' },
    },
    {
      id: 'broken',
      labelKey: 'dashboardActionFirst.oppBrokenPages',
      count: stats.brokenErrors ?? 0,
      impact: 'Medium',
      navigate: { tab: 'seo-optimizer', subTab: 'overview' },
    },
    {
      id: 'meta',
      labelKey: 'dashboardActionFirst.oppMetadataIssues',
      count: stats.metaErrors ?? 0,
      impact: 'High',
      navigate: { tab: 'seo-optimizer', subTab: 'overview' },
    },
    {
      id: 'content',
      labelKey: 'dashboardActionFirst.oppContentOptimization',
      count: stats.contentErrors ?? 0,
      impact: 'Low',
      navigate: { tab: 'seo-optimizer', subTab: 'overview' },
    },
  ];
  return items.filter((o) => o.count > 0).sort((a, b) => b.count - a.count);
}

export function countHighImpactOpportunities(stats) {
  return computeTopOpportunities(stats).filter((o) => o.impact === 'High').length;
}

export function computeFeatureAdoption(stats) {
  const audited = (stats.pagesAudited?.current ?? 0) > 0 || Boolean(stats.cacheMetadata?.isCached);
  return [
    { id: 'audit', labelKey: 'dashboardActionFirst.featSiteAudit', activated: audited },
    { id: 'page-speed', labelKey: 'dashboardActionFirst.featPageSpeed', activated: false },
    {
      id: 'ai-seo',
      labelKey: 'dashboardActionFirst.featAiSeo',
      activated: audited && (stats.metaErrors > 0 || stats.contentErrors > 0 || stats.overallScore < 90),
    },
    { id: 'alt-text', labelKey: 'dashboardActionFirst.featAltText', activated: (stats.altTextGeneration?.generated ?? 0) > 0 },
    { id: 'image-opt', labelKey: 'dashboardActionFirst.featImageOpt', activated: (stats.imageOptimization?.optimized ?? 0) > 0 },
    { id: 'bulk-editor', labelKey: 'dashboardActionFirst.featBulkEditor', activated: false },
    { id: 'keywords', labelKey: 'dashboardActionFirst.featKeywords', activated: false },
  ];
}
